<template>
  <div class="pdv-dashboard">
    <!-- Status: grouped panels (premium layout) -->
    <div class="dashboard-grid">
      <div v-for="group in cardGroups" :key="group.title" class="dashboard-panel">
        <div class="panel-title">{{ group.title }}</div>
        <div class="panel-rows">
          <div
            v-for="item in group.items"
            :key="item.status"
            class="panel-row"
            :data-testid="`pdv-dashboard-card-${item.status}`"
          >
            <div class="row-icon" :class="`icon-${statusColor(item.status)}`">
              <i :class="statusIcon(item.status)"></i>
            </div>
            <span class="row-label">{{ item.label }}</span>
            <span class="row-value">{{ item.count }}</span>
          </div>
        </div>
      </div>

      <!-- Recebimentos: financial KPIs as a 4th column -->
      <div class="dashboard-panel">
        <div class="panel-title">{{ t('Recebimentos') }}</div>
        <div class="panel-rows">
          <div
            v-for="item in financialItems"
            :key="item.key"
            class="panel-row financial-row"
            :data-testid="`pdv-dashboard-financial-card-${item.key}`"
          >
            <div class="row-icon" :class="`icon-${item.color}`">
              <i :class="item.icon"></i>
            </div>
            <span class="row-label">{{ item.label }}</span>
            <span class="row-amount">{{ n(item.amount, 'currency') }}</span>
            <span class="row-value">{{ item.count }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCheckout } from '../../composables/useSOCheckout';
import { useSOStatusMap } from '../../composables/useSOStatusMap';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';

interface StatusConfig {
  label: string;
  [key: string]: unknown;
}

interface StatusItem {
  status: string;
  label: string;
  count: number;
}

interface StatusGroup {
  title: string;
  items: StatusItem[];
}

interface FinancialSummary {
  totalAReceber: number;
  totalRecebidoParcial: number;
  totalRecebido: number;
  totalGeral: number;
  countAReceber: number;
  countParcial: number;
  countRecebido: number;
  countTotal: number;
}

interface OrderRow {
  so_order_financial_status?: string;
  so_order_total?: number;
  so_order_total_recebido?: number;
  [key: string]: unknown;
}

interface TableRefLike {
  lastLoadedOrders?: { value?: unknown[] } | unknown[];
}

const props = defineProps<{
  tableRef?: TableRefLike | null;
}>();

const { t, n } = useI18n();
const { fetchDashboard } = useSOCheckout();
const { orderStatusMap } = useSOStatusMap();
const dashboardData = ref<Record<string, number> | null>(null);
const allOrders = ref<OrderRow[]>([]);

const getStatusCount = (status: string): number => dashboardData.value?.[status] || 0;

const makeItem = (status: string): StatusItem => ({
  status,
  label: (orderStatusMap as Record<string, StatusConfig>)[status]?.label || status,
  count: getStatusCount(status)
});

const cardGroups = computed<StatusGroup[]>(() => [
  {
    title: 'Status Geral',
    items: ['draft', 'aguardando_laudos_entrada', 'em_producao'].map(makeItem)
  },
  {
    title: 'Finalização',
    items: ['aguardando_laudos_saida', 'pronto_para_entrega', 'parcialmente_entregue'].map(makeItem)
  },
  {
    title: 'Entrega & Status',
    items: ['entregue', 'cancelled'].map(makeItem)
  }
]);

const statusColor = (status: string): string => {
  const map: Record<string, string> = {
    draft: 'yellow',
    aguardando_laudos_entrada: 'blue',
    em_producao: 'blue',
    aguardando_laudos_saida: 'blue',
    pronto_para_entrega: 'green',
    parcialmente_entregue: 'yellow',
    entregue: 'green',
    cancelled: 'red'
  };
  return map[status] || 'surface';
};

const statusIcon = (status: string): string => {
  const map: Record<string, string> = {
    draft: 'pi pi-inbox',
    aguardando_laudos_entrada: 'pi pi-file-search',
    em_producao: 'pi pi-cog',
    aguardando_laudos_saida: 'pi pi-file-check',
    pronto_para_entrega: 'pi pi-check-circle',
    parcialmente_entregue: 'pi pi-minus-circle',
    entregue: 'pi pi-box',
    cancelled: 'pi pi-times-circle'
  };
  return map[status] || 'pi pi-circle';
};

interface FinancialItem {
  key: string;
  label: string;
  count: number;
  amount: number;
  color: string;
  icon: string;
}

const financialItems = computed<FinancialItem[]>(() => {
  const s = financialSummary.value;
  return [
    { key: 'areceber', label: t('A Receber'), count: s.countAReceber, amount: s.totalAReceber, color: 'yellow', icon: 'pi pi-clock' },
    { key: 'parcial', label: t('Parcial'), count: s.countParcial, amount: s.totalRecebidoParcial, color: 'blue', icon: 'pi pi-minus-circle' },
    { key: 'recebido', label: t('Recebido'), count: s.countRecebido, amount: s.totalRecebido, color: 'green', icon: 'pi pi-check-circle' },
    { key: 'total', label: t('Total'), count: s.countTotal, amount: s.totalGeral, color: 'surface', icon: 'pi pi-wallet' }
  ];
});

const financialSummary = computed<FinancialSummary>(() => {
  const aReceber = allOrders.value.filter((o) => o.so_order_financial_status === 'a_receber');
  const parcial = allOrders.value.filter((o) => o.so_order_financial_status === 'recebimento_parcial');
  const recebido = allOrders.value.filter((o) => o.so_order_financial_status === 'recebido');

  return {
    totalAReceber: aReceber.reduce((s, o) => s + (Number(o.so_order_total) || 0), 0),
    totalRecebidoParcial: parcial.reduce((s, o) => s + (Number(o.so_order_total_recebido) || 0), 0),
    totalRecebido: recebido.reduce((s, o) => s + (Number(o.so_order_total) || 0), 0),
    totalGeral: allOrders.value.reduce((s, o) => s + (Number(o.so_order_total) || 0), 0),
    countAReceber: aReceber.length,
    countParcial: parcial.length,
    countRecebido: recebido.length,
    countTotal: allOrders.value.length
  };
});

const loadData = async (): Promise<void> => {
  log('PDVDashboard', 'loadData', 'Fetching dashboard data');
  try {
    dashboardData.value = await fetchDashboard();
  } catch (err) {
    logError('PDVDashboard', 'loadData', 'Error', err);
  }
};

const handleOrdersLoaded = (data?: OrderRow[]): void => {
  log('PDVDashboard', 'handleOrdersLoaded', 'Orders loaded', data?.length);
  allOrders.value = data || [];
};

const pullFromTableRef = (): void => {
  const tableRef = props.tableRef;
  if (!tableRef) return;
  const exposed = tableRef.lastLoadedOrders;
  // exposed could be a ref ({ value: unknown[] }) or a plain array depending on parent wiring.
  let rows: OrderRow[] | undefined;
  if (Array.isArray(exposed)) {
    rows = exposed as OrderRow[];
  } else if (exposed && Array.isArray((exposed as { value?: unknown[] }).value)) {
    rows = (exposed as { value: unknown[] }).value as OrderRow[];
  }
  if (rows && rows.length > 0) {
    log('PDVDashboard', 'pullFromTableRef', 'Seeding allOrders from tableRef.lastLoadedOrders', rows.length);
    allOrders.value = rows;
  }
};

onMounted(() => {
  loadData();
  pullFromTableRef();
  eventBus.on('pdv.orders-loaded', handleOrdersLoaded);
});

onBeforeUnmount(() => {
  eventBus.off('pdv.orders-loaded', handleOrdersLoaded);
});

defineExpose({ loadData });
</script>

<style scoped>
.pdv-dashboard {
  margin-bottom: 16px;
}

/* ── Status: grouped panels ── */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.dashboard-panel {
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 16px;
}

.panel-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--app-text-secondary-color);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--p-surface-200);
}

.panel-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.panel-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.row-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.8rem;
}

.row-label {
  flex: 1;
  font-size: 0.85rem;
  color: var(--app-text-color);
}

.row-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--app-text-color);
}

/* Recebimentos column: label + amount + count badge */
.financial-row .row-amount {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
  white-space: nowrap;
}

.financial-row .row-value {
  min-width: 1.75rem;
  text-align: right;
  font-size: 1rem;
}

/* Icon colors */
.icon-yellow { background: var(--p-yellow-100); color: var(--p-yellow-700); }
.icon-blue { background: var(--p-blue-100); color: var(--p-blue-700); }
.icon-green { background: var(--p-green-100); color: var(--p-green-700); }
.icon-red { background: var(--p-red-100); color: var(--p-red-700); }
.icon-surface { background: var(--p-surface-100); color: var(--p-text-muted-color); }

@media (max-width: 768px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
